import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../assets/logo.png';
import { AuthContext } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const { 
    getFormattedBalances,
    isLoading: walletLoading,
    error: walletError,
    fetchBalance 
  } = useWallet();
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const location = useLocation();

  // Get formatted balances
  const formattedBalances = getFormattedBalances();

  // Navigation links
  const navLinks = [
    { path: '/sports', label: 'Sports', icon: '⚽' },
    { path: '/live', label: 'Live', icon: '🔥' },
    { path: '/casino', label: 'Casino', icon: '🎰' },
    { path: '/betslip', label: 'Betslip', icon: '📋' },
    { path: '/wallet', label: 'Wallet', icon: '💰' },
    { path: '/profile', label: 'Profile', icon: '👤' },
  ];

  // Check if a link is active
  const isActive = (path) => {
    return location.pathname === path || 
           (path !== '/' && location.pathname.startsWith(path));
  };

  // Toggle mobile menu function
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  // Handle click outside for both dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hamburgerRef.current && hamburgerRef.current.contains(event.target)) {
        return;
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle wallet error - refresh on click
  const handleBalanceClick = async () => {
    if (walletError) {
      try {
        await fetchBalance();
      } catch (error) {
        // console.error('Failed to refresh balance:', error);
      }
    }
  };

  const renderBalanceDisplay = (isMobile = false) => {
    if (walletError) {
      return (
        <button
          onClick={handleBalanceClick}
          className={`${isMobile ? 'w-full' : ''} px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors`}
          title="Click to retry"
        >
          ❌ Error
        </button>
      );
    }

    if (walletLoading) {
      return (
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      );
    }

    return (
      <>
        <div className={`${isMobile ? 'w-1/2' : ''} px-3 py-1 bg-blue-800 text-white rounded text-sm font-medium whitespace-nowrap`}>
          <span className="font-bold">${formattedBalances.available}</span>
        </div>
        <div className={`${isMobile ? 'w-1/2' : ''} px-3 py-1 bg-cyan-600 text-white rounded text-sm font-medium whitespace-nowrap`}>
          <span className="font-bold">Locked: ${formattedBalances.locked}</span>
        </div>
      </>
    );
  };

  return (
    <header className="bg-gray-900 text-white fixed top-0 left-0 w-full h-16 shadow-lg z-50">
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
          <img 
            src={Logo} 
            alt="DiceAce12 Logo" 
            className="h-10 w-auto" 
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center space-x-1 px-3 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(link.path)
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <span>{link.icon}</span>
              <span>{link.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="flex items-center space-x-2">
          {user ? (
            <>
              {/* Balance Display - Desktop */}
              <div className="hidden md:flex items-center space-x-2">
                {renderBalanceDisplay()}
              </div>

              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button 
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-800 text-white rounded text-sm font-medium hover:bg-gray-700 transition-colors whitespace-nowrap"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-center text-xs">
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="hidden md:inline">{user.username || 'User'}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded shadow-lg py-1 z-50 border border-gray-700">
                    <div className="px-4 py-2 border-b border-gray-700">
                      <p className="text-sm font-medium text-white">{user.username || 'User'}</p>
                      <p className="text-xs text-gray-400 truncate">{user.email || ''}</p>
                      {!walletError && !walletLoading && (
                        <div className="mt-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Balance:</span>
                            <span className="text-green-400">${formattedBalances.available}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Locked:</span>
                            <span className="text-yellow-400">${formattedBalances.locked}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span>👤</span>
                      <span>My Profile</span>
                    </Link>
                    <Link
                      to="/wallet"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span>💰</span>
                      <span>My Wallet</span>
                    </Link>
                    <Link
                      to="/betslip"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span>📋</span>
                      <span>My Betslip</span>
                    </Link>
                    <div className="border-t border-gray-700 my-1"></div>
                    <button
                      onClick={() => {
                        logout();
                        setIsDropdownOpen(false);
                      }}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
                    >
                      <span>🚪</span>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Desktop Login/Sign Up buttons (show only on desktop)
            <div className="hidden md:flex items-center space-x-2">
              <Link
                to="/login"
                className="px-3 py-1.5 bg-blue-800 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="px-3 py-1.5 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 transition-colors"
              >
                Sign Up
              </Link>
            </div>
          )}

          {/* Mobile Menu Button - ONLY on mobile */}
          <button
            ref={hamburgerRef}
            className="md:hidden ml-2 p-2 rounded hover:bg-gray-800"
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu - ONLY on mobile */}
      {isMobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          className="md:hidden absolute top-16 left-0 right-0 bg-gray-900 border-t border-gray-800 shadow-lg z-40"
        >
          <div className="container mx-auto px-4 py-3">
            {/* Mobile Navigation Links */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium ${
                    isActive(link.path)
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              ))}
            </div>

            {/* Login/Sign Up in Mobile Menu (when not logged in) */}
            {!user ? (
              <div className="flex flex-col gap-2 border-t border-gray-800 pt-3 mt-3">
                <div className="text-center text-gray-400 text-sm mb-2">
                  Welcome! Please login to continue
                </div>
                <Link
                  to="/login"
                  className="px-3 py-2.5 bg-blue-800 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-3 py-2.5 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 transition-colors text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            ) : (
              /* Mobile Balance Display (if logged in) */
              <div className="border-t border-gray-800 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2 gap-2">
                  {renderBalanceDisplay(true)}
                </div>
                <div className="text-center text-xs text-gray-400 mt-2">
                  Welcome, {user.username || 'User'}!
                </div>
                <button
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 mt-3 bg-red-600/20 text-red-400 rounded text-sm font-medium hover:bg-red-600/30 transition-colors border border-red-600/30"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;